import { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { LectureAiChip, NormalizedSourceCard, SurfaceHero } from '../components/design-kit';
import { BookOpenText, Eye, FileUp, Loader2, RefreshCw, Share2, Sparkles, Trash2 } from 'lucide-react';

function statusTone(status) {
  if (status === 'normalized') return 'default';
  if (status === 'processing') return 'secondary';
  if (status === 'upload_complete') return 'default';
  if (status === 'failed') return 'destructive';
  if (status === 'reupload_required') return 'destructive';
  if (status === 'metadata_only') return 'secondary';
  if (status === 'text_extracted') return 'default';
  return 'outline';
}

export default function LibraryPage() {
  const [documents, setDocuments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [extractingId, setExtractingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [title, setTitle] = useState('');
  const [subjectId, setSubjectId] = useState('none');
  const [files, setFiles] = useState([]);

  const sortedDocuments = useMemo(
    () => [...documents].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [documents]
  );

  const loadLibrary = async () => {
    try {
      const [documentResult, subjectResult] = await Promise.all([
        api.getDocuments(),
        api.getSubjects(),
      ]);
      setDocuments(documentResult.documents || []);
      setSubjects(subjectResult || []);
    } catch (error) {
      console.error('Failed to load library:', error);
      toast.error(error.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLibrary();
  }, []);

  useEffect(() => {
    const hasActiveProcessing = documents.some(
      (document) => document.normalization_status === 'processing' || document.status === 'processing'
    );
    if (!hasActiveProcessing) return undefined;

    const intervalId = window.setInterval(() => {
      loadLibrary();
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [documents]);

  const resetForm = () => {
    setTitle('');
    setSubjectId('none');
    setFiles([]);
    const input = document.getElementById('library-upload-input');
    if (input) input.value = '';
  };

  const uploadSingleFile = async (uploadFile, uploadTitle) => {
    const createResult = await api.createDocument({
      title: uploadTitle,
      original_filename: uploadFile.name,
      mime_type: uploadFile.type || 'application/octet-stream',
      subject_id: subjectId === 'none' ? null : subjectId,
    });

    const { document, upload } = createResult;

    if (upload?.url) {
      const uploadResponse = await fetch(upload.url, {
        method: upload.method || 'PUT',
        headers: {
          'Content-Type': uploadFile.type || 'application/octet-stream',
        },
        body: uploadFile,
      });

      if (!uploadResponse.ok) {
        throw new Error('Storage upload failed');
      }

      const completeResult = await api.completeDocumentUpload(document.id, {
        byte_size: uploadFile.size,
        wait_for_normalization: false,
      });
      setDocuments((prev) => [completeResult.document, ...prev.filter((item) => item.id !== document.id)]);
      return completeResult;
    }

    const parsed = await api.parseDocument(uploadFile);
    await api.deleteDocument(document.id);
    const textDoc = await api.createTextDocument({
      title: uploadTitle,
      original_filename: uploadFile.name,
      mime_type: uploadFile.type || 'text/plain',
      subject_id: subjectId === 'none' ? null : subjectId,
      content: parsed.content,
    });
    setDocuments((prev) => [textDoc.document, ...prev.filter((item) => item.id !== document.id)]);
    return { document: textDoc.document };
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (files.length === 0) {
      toast.error('Choose at least one file to upload');
      return;
    }

    setUploading(true);
    try {
      let uploadedCount = 0;
      let readyCount = 0;
      let backgroundProcessingCount = 0;
      const warnings = [];

      for (const uploadFile of files) {
        const uploadTitle = files.length === 1 && title.trim() ? title.trim() : uploadFile.name;
        const result = await uploadSingleFile(uploadFile, uploadTitle);
        uploadedCount += 1;
        if (result.document?.normalization_status === 'ready') {
          readyCount += 1;
        }
        if (result.document?.normalization_status === 'processing') {
          backgroundProcessingCount += 1;
        }
        if (result.normalization_error) {
          warnings.push(`${uploadFile.name}: ${result.normalization_error}`);
        }
      }

      if (files.length === 1) {
        toast.success(
          readyCount === 1
            ? 'File uploaded and normalized for AI'
            : backgroundProcessingCount === 1
            ? 'File uploaded. AI processing continues in the background.'
            : 'File uploaded to your library'
        );
      } else {
        const summary = readyCount > 0 || backgroundProcessingCount > 0
          ? `${uploadedCount} files uploaded. ${readyCount} ready, ${backgroundProcessingCount} still processing.`
          : `${uploadedCount} files uploaded to your library`;
        toast.success(summary);
      }
      warnings.forEach((warning) => toast.error(`Normalization warning: ${warning}`));
      resetForm();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleExtractText = async (documentId) => {
    setExtractingId(documentId);
    try {
      const result = await api.extractDocumentText(documentId);
      toast.success(`Normalized ${result.length} characters for AI context`);
      await loadLibrary();
    } catch (error) {
      console.error('Extraction failed:', error);
      toast.error(error.message || 'Failed to normalize document');
    } finally {
      setExtractingId(null);
    }
  };

  const buildDocumentPreviewUrl = (document) => `${window.location.origin}/shared/document/${document.id}`;

  const handleCopyShareLink = async (document) => {
    try {
      await navigator.clipboard.writeText(buildDocumentPreviewUrl(document));
      toast.success('Shared preview link copied');
    } catch (error) {
      toast.error('Failed to copy share link');
    }
  };

  const handleDeleteDocument = async (document) => {
    const confirmed = window.confirm(`Delete "${document.title}" from your library?`);
    if (!confirmed) return;
    setDeletingId(document.id);
    try {
      await api.deleteDocument(document.id);
      setDocuments((prev) => prev.filter((item) => item.id !== document.id));
      toast.success('Document deleted');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.message || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <SurfaceHero
          eyebrow="Library"
          title="Notes, PDFs, and source material"
          detail="Keep the documents you want the app to remember in one place. Each source is normalized into a model-friendly format so stronger GPT and Grok routes can reason over the same shared context."
          aside={(
            <Button type="button" variant="outline" onClick={loadLibrary} disabled={loading || uploading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          )}
        />

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <Card className="border-border/60 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-serif text-xl">
                <BookOpenText className="h-5 w-5" />
                Your documents
              </CardTitle>
              <CardDescription>
                Uploaded files already tied to your account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading your library...
                </div>
              ) : sortedDocuments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No documents yet. Upload your first PDF, note export, or study guide.
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedDocuments.map((document) => (
                    <div key={document.id} className="rounded-xl border border-border/70 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-foreground">{document.title}</p>
                            <Badge variant={statusTone(document.status)}>{document.status}</Badge>
                            {document.normalization_status === 'ready' ? <Badge variant="secondary">Normalized for AI</Badge> : null}
                            {document.normalization_status === 'processing' ? <Badge variant="outline">Processing in background</Badge> : null}
                            {document.extra_metadata?.source_url ? (
                              <LectureAiChip
                                label={document.extra_metadata.source_url}
                                tone="link"
                                className="max-w-full"
                              />
                            ) : null}
                          </div>
                          <div className="mt-2">
                            <LectureAiChip
                              label={document.original_filename}
                              tone="document"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Added {document.created_at ? formatDistanceToNow(new Date(document.created_at), { addSuffix: true }) : 'recently'}
                          </p>
                          {document.normalization?.error ? (
                            <p className="text-xs text-destructive">
                              Parse issue: {document.normalization.error}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {document.normalization_status !== 'ready' && document.status !== 'processing' && document.storage_backend === 'r2' ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleExtractText(document.id)}
                              disabled={extractingId === document.id}
                            >
                              {extractingId === document.id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Sparkles className="mr-2 h-4 w-4" />
                              )}
                              Normalize now
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(buildDocumentPreviewUrl(document), '_blank', 'noopener,noreferrer')}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Preview
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyShareLink(document)}
                          >
                            <Share2 className="mr-2 h-4 w-4" />
                            Copy share link
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteDocument(document)}
                            disabled={deletingId === document.id}
                          >
                            {deletingId === document.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="mr-2 h-4 w-4" />
                            )}
                            Delete
                          </Button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <NormalizedSourceCard document={document} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-serif text-xl">
                <FileUp className="h-5 w-5" />
                Add a document
              </CardTitle>
              <CardDescription>
                Upload one or many files now. Once the raw upload finishes, AI normalization keeps running in the background so you can move on.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleUpload}>
                <div className="space-y-2">
                  <Label htmlFor="library-title">Title</Label>
                  <Input
                    id="library-title"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="Exam 2 study guide"
                  />
                  <p className="text-xs text-muted-foreground">
                    Custom titles apply to single uploads. Multi-file uploads keep each filename as its document title.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="library-subject">Subject</Label>
                  <Select value={subjectId} onValueChange={setSubjectId}>
                    <SelectTrigger id="library-subject">
                      <SelectValue placeholder="Optional subject" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No subject</SelectItem>
                      {subjects.map((subject) => (
                        <SelectItem key={subject.id} value={subject.id}>
                          {subject.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="library-upload-input">Files</Label>
                  <Input
                    id="library-upload-input"
                    type="file"
                    multiple
                    onChange={(event) => setFiles(Array.from(event.target.files || []))}
                  />
                  {files.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {files.map((selectedFile) => (
                        <LectureAiChip
                          key={`${selectedFile.name}-${selectedFile.lastModified}`}
                          label={selectedFile.name}
                          tone="document"
                        />
                      ))}
                    </div>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    PDFs work best today. Other file types can still be stored and indexed in later slices.
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={uploading}>
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
                  {uploading ? `Uploading ${files.length || 1} ${files.length === 1 ? 'file' : 'files'}` : 'Add to library'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
